import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrophy, faHandshake, faHardHat } from '@fortawesome/free-solid-svg-icons';

interface CounterProps {
  end: number;
  duration: number;
}

const Counter: React.FC<CounterProps> = ({ end, duration }) => {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true });
  
  useEffect(() => {
    if (isInView) {
      let startTime: number | null = null;
      const step = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);
        setCount(Math.floor(progress * end));
        
        if (progress < 1) {
          requestAnimationFrame(step);
        }
      };
      
      requestAnimationFrame(step);
    }
  }, [isInView, end, duration]);
  
  return <div ref={ref}>{count}</div>;
};

const AboutSection: React.FC = () => {
  const stats = [
    { 
      icon: faTrophy, 
      value: 25, 
      label: 'Années d\'expérience',
      color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
    },
    { 
      icon: faHandshake, 
      value: 200, 
      label: 'Clients satisfaits',
      color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
    },
    { 
      icon: faHardHat, 
      value: 500, 
      label: 'Projets réalisés',
      color: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300'
    },
  ];

  return (
    <section className="py-20 bg-white dark:bg-gray-800">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 dark:text-white mb-6">
              Notre histoire et notre expertise
            </h2>
            <p className="text-lg text-gray-700 dark:text-gray-300 mb-6 leading-relaxed">
              Depuis notre création, nous nous sommes engagés à fournir des services de couverture et d'étanchéité de la plus haute qualité. Notre équipe d'experts qualifiés combine savoir-faire traditionnel et techniques modernes pour garantir des résultats exceptionnels.
            </p>
            <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
              Nous sommes fiers de notre réputation d'excellence et de notre capacité à répondre aux besoins uniques de chaque client, qu'il s'agisse de projets résidentiels ou commerciaux.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-3 gap-6 w-full">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`${stat.color} rounded-lg p-6 text-center flex flex-col items-center justify-center h-full shadow-md`}
              >
                <FontAwesomeIcon icon={stat.icon} className="text-3xl mb-4" />
                <div className="text-4xl font-bold mb-2 flex items-center justify-center">
                  <Counter end={stat.value} duration={2000} />
                  <span>+</span>
                </div>
                <div className="font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
